/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import useTryCatch from "hooks/useTryCatch";
import { updatePreferences } from "lib/preferencesApi";
import useCompanion from "hooks/useCompanion";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import useEventRouting from "hooks/useEventRouting";
import ROUTES from "routes";
import { useUser } from "state/UserContext";
import {
  Dairy,
  Egg,
  Fish,
  Nuts,
  Peanuts,
  Sesame,
  Shellfish,
  Wheat,
} from "assets/allergies";
import { Button, Card, Input, Progress, Stack } from "components";
import { PageLayout } from "templates";
import { getUserId } from "Models/User";
import {
  appendOtherOptions,
  safeAppend,
} from "pages/Dashboard/GuestDetailsPage/GuestPreferencesPage";
import { useEffect, useRef } from "react";
import useAllergies, { ALLERGIES_OPTIONS } from "./useAllergies";
import { usePrefNavigator } from "state/PrefNavigatorContext";
import useRouting from "hooks/useRouting";
import usePreviousRoute from "utils/usePreviousRoute";
import { useTranslation } from "react-i18next";
import { pushToDataLayer } from "utils/gtm";

const AllergiesIconsMapper = (name) =>
  ({
    egg: Egg,
    dairy: Dairy,
    peanuts: Peanuts,
    nuts: Nuts,
    fish: Fish,
    shellfish: Shellfish,
    wheat: Wheat,
    sesame: Sesame,
    other: null,
  })[name];

const Allergies = () => {
  const { t } = useTranslation();
  const { userData, setLoading, isLoading } = useUser();
  const { userId } = useParams();
  const [companion, setCompanion] = useCompanion(userId);
  const { navigateWithEvent, eventId } = useEventRouting();
  const { shouldEnterDonePage } = useRouting();
  const goBack = usePreviousRoute();
  const {
    isOtherAllergy,
    customAllergy,
    handleOnChange,
    formData,
    setCustomAllergy,
  } = useAllergies(companion);

  const {
    progressBarValues,
    getNextStepName,
    getPreviousStepName,
    setCurrentStep,
  } = usePrefNavigator();

  useEffect(() => {
    setCurrentStep("allergies");
  }, []);

  const onContinue = () => {
    const nextStepName = getNextStepName();
    if (nextStepName && ROUTES.choices[nextStepName]) {
      const routePath = ROUTES.choices[nextStepName](userId);
      navigateWithEvent(routePath);
    } else {
      return null;
    }
  };

  const onGoBack = () => {
    pushToDataLayer({
      event: "preferences_back",
      screen_name: "food allergies",
      journey_step: "food allergies",
    });
    const previousStepName = getPreviousStepName();
    if (previousStepName == null) {
      navigateWithEvent(ROUTES.choices.base(userId));
      return;
    }
    goBack();
  };

  const onSkip = async () => {
    pushToDataLayer({
      event: "skip_food_allergies",
      screen_name: "food allergies",
      journey_step: "food allergies",
    });
    const nextStepName = getNextStepName();
    if (nextStepName == null) {
      navigateWithEvent(ROUTES.onboard.done);
    } else {
      onContinue();
    }
  };

  const handleOnSubmit = useTryCatch(async (e) => {
    e.preventDefault();
    pushToDataLayer({
      event: "next_food_allergies",
      screen_name: "food allergies",
      journey_step: "food allergies",
    });
    // Input doesn't forward `required` to the DOM, so validate manually
    if (isOtherAllergy && !customAllergy.trim()) {
      return toast.error(t("common.messages.requiredField"));
    }
    let result = "";

    Object.values(formData).forEach(({ state, value }, i) => {
      if (state === "success" && value !== "") {
        // if (result.length === 0) {
        //   result += value;
        //   return result;
        // }
        // result += "," + value;
        if (value !== 0) {
          result = safeAppend(result, value, ",");
        }
      }
      return result;
    });
    if (customAllergy !== "") {
      result = appendOtherOptions(result, customAllergy);
    }
    if (result.length === 0) {
      return toast.error("Please choose an option.");
    }

    setLoading(true);
    const id = getUserId(companion) || userId;
    await updatePreferences(
      id,
      {
        isCompanion: id !== getUserId(userData),
        allergies: result,
      },
      eventId,
    );
    setLoading(false);

    const newCompanions = setCompanion({
      ...companion,
      allergies: result,
    });
    const nextStepName = getNextStepName();
    if (nextStepName === null) {
      const goTo = await shouldEnterDonePage(newCompanions);
      navigateWithEvent(goTo);
    } else {
      onContinue();
    }
  }, setLoading);

  const formRef = useRef(null);

  const isInitial =
    ALLERGIES_OPTIONS.every((key) => formData?.[key]?.state === "initial") &&
    !isOtherAllergy;
  return (
    <PageLayout
      title={t("allergies.title")}
      note={t("allergies.description")}
      hasGoBack
      goBackMethod={onGoBack}
      hasPolicy={false}
      hasProgressBar
      progressBarValues={progressBarValues}
    >
      <Stack component="article" className="mt-10 max-w-[400px] mx-auto">
        <form
          ref={formRef}
          onSubmit={handleOnSubmit}
          className="justify-between sm:justify-center min-h-full"
        >
          <Stack>
            <div className="grid grid-cols-3 gap-2">
              {ALLERGIES_OPTIONS.map((key) => (
                <Card
                  title={t(`allergies.${key}`)}
                  key={key}
                  variant="allergy"
                  media={AllergiesIconsMapper(key)}
                  state={formData[key]?.state}
                  onClick={() => handleOnChange(key)}
                />
              ))}
            </div>
            <div>
              {isOtherAllergy && (
                <Input
                  placeholder={t("allergies.otherPlaceholder")}
                  variant="custom"
                  name="other"
                  required
                  label={t("allergies.otherLabel")}
                  type="text"
                  className="mt-5"
                  value={customAllergy}
                  onChange={({ target: { value } }) => setCustomAllergy(value)}
                  spellCheck={false}
                />
              )}
            </div>
          </Stack>
          <Stack className="mt-8">
            <div className="flex sm:hidden gap-x-0.5">
              {progressBarValues.map((value, index) => (
                <Progress key={index} value={value} />
              ))}
            </div>
            <div className="mt-2 w-full">
              <Button
                variant="primary"
                onClick={(e) => {
                  isInitial ? onSkip() : formRef.current?.requestSubmit();
                }}
                isLoading={isLoading}
                fullWidth
              >
                {isInitial
                  ? t("allergies.noAllergiesOptionLabel")
                  : t("allergies.continueButtonLabel")}
              </Button>
            </div>
          </Stack>
        </form>
      </Stack>
    </PageLayout>
  );
};
export default Allergies;
